/**
 * Client-side "proxy SDK".
 *
 * This module exposes an object with (almost) the same interface as the
 * browser Sharetribe SDK instance created via `sdkLoader.createInstance(...)`,
 * but every call is routed through our own Express server instead of talking
 * to Marketplace API directly. The server holds the user's Sharetribe token
 * in an httpOnly session (see docs/BFF_IMPLEMENTATION_PLAN.md) so the token
 * itself never reaches the browser.
 *
 * Because the interface mirrors the real SDK, the ~15 duck files that call
 * `sdk.listings.query(...)`, `sdk.currentUser.show(...)`, `sdk.images.upload(...)`,
 * `sdk.login(...)`, etc. do not need to change: `createProxySdk()` is a drop-in
 * replacement for `createInstance(...)` at the single instantiation site in
 * `src/index.js`.
 *
 * Wire contract (implemented server-side, see server/apiRouter.js):
 *  - POST /api/sdk                 { resource, op, args } -> transit {status, statusText, data}
 *  - POST /api/sdk/images/upload   multipart/form-data     -> transit {status, statusText, data}
 *  - POST /api/auth/login          JSON {username, password} -> 204
 *  - POST /api/auth/logout         (no body)                 -> 204
 *  - POST /api/auth/signup         JSON {email, password, ...} -> 204
 *  - GET  /api/auth/info           200 JSON authInfo() result
 *
 * Error shape parity: on failure the server returns a JSON LocalAPIError body
 * (same shape as the existing privileged endpoints). We convert that into an
 * `Error` with `status`, `statusText`, and `data` properties attached, exactly
 * like `src/util/api.js`'s `request()` does. That's the shape `storableError`
 * (src/util/errors.js) and the duck files' `.catch(e => ...)` handlers expect.
 */

import appSettings from '../config/settings';
import { transit } from './sdkLoader';
import { typeHandlers, apiBaseUrl } from './api';

const SDK_ENDPOINT = '/api/sdk';
const SDK_IMAGES_UPLOAD_ENDPOINT = '/api/sdk/images/upload';
const AUTH_LOGIN_ENDPOINT = '/api/auth/login';
const AUTH_LOGOUT_ENDPOINT = '/api/auth/logout';
const AUTH_SIGNUP_ENDPOINT = '/api/auth/signup';
const AUTH_INFO_ENDPOINT = '/api/auth/info';

const isBrowser = () => typeof window !== 'undefined';

const serializeTransit = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserializeTransit = str => {
  return transit.read(str, { typeHandlers });
};

/**
 * Turn a non-2xx fetch Response into an Error carrying {status, statusText, data},
 * matching the shape produced by `src/util/api.js`'s `request()` for the existing
 * privileged endpoints (LocalAPIError JSON body -> Error with same fields).
 */
const throwLocalApiError = res => {
  return res
    .json()
    .catch(() => ({})) // non-JSON error body (e.g. the transit middleware's plain-text 400)
    .then(data => {
      const e = new Error(data.message || data.error || res.statusText);
      Object.assign(e, data);
      // Proxy-specific rejections (allow-list/CSRF 403s, malformed-body 400s)
      // respond with a bare {error} body rather than the full LocalAPIError
      // shape — normalize so callers ALWAYS get {status, statusText, data},
      // as storableError and the duck error handlers expect.
      if (e.status === undefined) {
        e.status = res.status;
      }
      if (!e.statusText) {
        e.statusText = data.error || res.statusText;
      }
      if (!e.data || typeof e.data !== 'object') {
        e.data = { errors: [] };
      }
      throw e;
    });
};

/**
 * Parse a successful response. The SDK proxy endpoints respond with
 * Content-Type application/transit+json (transit-encoded {status, statusText, data}).
 * Some (e.g. /api/auth/login, /api/auth/logout, /api/auth/signup) simply respond
 * 204 with no body.
 */
const parseSuccessResponse = res => {
  const contentTypeHeader = res.headers.get('Content-Type');
  const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

  if (res.status === 204) {
    return Promise.resolve();
  }
  if (contentType === 'application/transit+json') {
    return res.text().then(deserializeTransit);
  }
  if (contentType === 'application/json') {
    return res.json();
  }
  return res.text();
};

const fetchJson = (path, options = {}) => {
  if (!isBrowser() || !window.fetch) {
    // The proxy SDK only runs in the browser. During SSR the server-side SDK
    // (server/api-util/sdk.js) is used directly, so this should never be hit,
    // but guard the same way src/util/api.js does.
    return Promise.reject(new Error('Fetch is not available during server-side rendering'));
  }

  const url = `${apiBaseUrl()}${path}`;
  const fetchOptions = {
    credentials: 'include',
    ...options,
  };

  return window.fetch(url, fetchOptions).then(res => {
    if (res.status >= 400) {
      return throwLocalApiError(res);
    }
    return parseSuccessResponse(res);
  });
};

/**
 * POST helper for the generic /api/sdk proxy endpoint and the /api/auth/*
 * JSON endpoints. Serializes `body` as transit (same encoding the server's
 * `serialize()` expects and the existing privileged endpoints already use),
 * unless `json: true` is given, in which case `body` is sent as plain JSON
 * (used for /api/auth/login and /api/auth/signup, which are not SDK calls
 * and don't need UUID/Money fidelity).
 */
const postRequest = (path, body, { json = false } = {}) => {
  const headers = json
    ? { 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/transit+json' };
  const serializedBody = json ? JSON.stringify(body) : serializeTransit(body);

  return fetchJson(path, {
    method: 'POST',
    headers,
    body: serializedBody,
  });
};

/**
 * Build a generic method for a given resource/op pair, e.g. ('listings', 'query').
 * Top-level ops (assetsByAlias, assetByAlias, assetsByVersion, assetByVersion)
 * pass `resource: null`.
 *
 * `args` is the exact argument array the browser SDK method would receive
 * (e.g. [params, queryParams] for `sdk.listings.query(params, queryParams)`,
 * or [{id, transition, params}, {expand:true}] for `sdk.transactions.transition(...)`).
 * Transit-serializing the whole array preserves UUID/Money/Decimal instances.
 */
const createSdkMethod = (resource, op) => (...args) => {
  return postRequest(SDK_ENDPOINT, { resource, op, args });
};

const RESOURCES = {
  listings: ['query', 'show', 'open', 'publish'],
  ownListings: [
    'query',
    'show',
    'create',
    'createDraft',
    'update',
    'publishDraft',
    'discardDraft',
    'delete',
    'close',
    'open',
  ],
  currentUser: [
    'show',
    'updateProfile',
    'changeEmail',
    'changePassword',
    'sendVerificationEmail',
    'verifyEmail',
    // NOTE: 'create' is intentionally NOT included here — it is handled by
    // the dedicated `currentUser.create` override below, which routes to
    // POST /api/auth/signup (see wire contract).
  ],
  transactions: [
    'query',
    'show',
    'initiate',
    'initiateSpeculative',
    'transition',
    'transitionSpeculative',
  ],
  processTransitions: ['query'],
  messages: ['query', 'send'],
  reviews: ['query', 'show'],
  users: ['show'],
  timeslots: ['query'],
  availabilityExceptions: ['query', 'create', 'delete'],
  stock: ['compareAndSet'],
  stripeAccount: ['create', 'update', 'fetch'],
  stripeAccountLinks: ['create'],
  stripeCustomer: ['create', 'addPaymentMethod', 'deletePaymentMethod'],
  stripeSetupIntents: ['create'],
  passwordReset: ['request', 'reset'],
};

/**
 * Top-level (non-namespaced) SDK operations that go through the generic
 * proxy with `resource: null`.
 */
const TOP_LEVEL_OPS = ['assetsByAlias', 'assetByAlias', 'assetsByVersion', 'assetByVersion'];

const buildResourceFacades = () => {
  const facades = {};
  Object.entries(RESOURCES).forEach(([resource, ops]) => {
    facades[resource] = {};
    ops.forEach(op => {
      facades[resource][op] = createSdkMethod(resource, op);
    });
  });
  return facades;
};

const buildTopLevelOps = () => {
  const ops = {};
  TOP_LEVEL_OPS.forEach(op => {
    ops[op] = createSdkMethod(null, op);
  });
  return ops;
};

/**
 * login({ username, password }) -> POST /api/auth/login -> 204.
 *
 * Parity check (src/ducks/auth.duck.js `loginThunk`): the duck only calls
 * `.then(() => dispatch(fetchCurrentUser(...)))` — the resolved value of
 * `sdk.login(...)` itself is discarded. We still resolve a browser-SDK-like
 * `{status, statusText, data}` shape for interface parity / future-proofing,
 * even though nothing currently reads it.
 */
const login = ({ username, password }) => {
  return postRequest(AUTH_LOGIN_ENDPOINT, { username, password }, { json: true }).then(() => ({
    status: 200,
    statusText: 'OK',
    data: {},
  }));
};

/**
 * logout() -> POST /api/auth/logout -> 204.
 *
 * Parity check (src/ducks/auth.duck.js `logoutThunk`): result is discarded
 * (`.then(() => { dispatch(clearCurrentUser()); ... })`).
 */
const logout = () => {
  return fetchJson(AUTH_LOGOUT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  }).then(() => ({
    status: 200,
    statusText: 'OK',
    data: {},
  }));
};

/**
 * authInfo() -> GET /api/auth/info -> 200 JSON.
 *
 * Parity check (src/ducks/auth.duck.js `authInfoThunk`): the duck reads
 * `payload.isAnonymous`, `payload.isLoggedInAs`, `payload.scopes` directly
 * off the resolved value (NOT off a `.data` wrapper) — same as the real
 * browser SDK's `sdk.authInfo()`, which resolves directly to
 * `{scopes?, isAnonymous, isLoggedInAs?, grantType?}`. So this must resolve
 * to the plain JSON body, not a {status, statusText, data} envelope.
 */
const authInfo = () => {
  return fetchJson(AUTH_INFO_ENDPOINT, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * currentUser.create(params) -> POST /api/auth/signup -> 204.
 *
 * Parity check (src/ducks/auth.duck.js `signupThunk`): result of
 * `sdk.currentUser.create(params)` is discarded — only used to sequence
 * into `dispatch(loginThunk(...))`. `params` already carries
 * {email, password, firstName, lastName, displayName?, bio?, publicData?,
 * protectedData?, privateData?} exactly as server's /api/auth/signup expects.
 */
const currentUserCreate = params => {
  return postRequest(AUTH_SIGNUP_ENDPOINT, params, { json: true }).then(() => ({
    status: 200,
    statusText: 'OK',
    data: {},
  }));
};

/**
 * images.upload(params, queryParams) -> POST /api/sdk/images/upload (multipart).
 *
 * Parity check: call sites (EditListingPage.duck.js, ProfileSettingsPage.duck.js)
 * do `sdk.images.upload({ image: file }, queryParams).then(resp => resp.data.data)`
 * so we must resolve {status, statusText, data}. `queryParams` (expand,
 * fields.image, imageVariant.* etc.) is transit-encoded into a form field so
 * that any non-JSON-safe values survive, mirroring how the generic proxy
 * transit-encodes `args`.
 */
const imagesUpload = (params, queryParams) => {
  if (!isBrowser() || !window.fetch) {
    return Promise.reject(new Error('Fetch is not available during server-side rendering'));
  }

  const formData = new FormData();
  formData.append('image', params.image);
  if (queryParams) {
    formData.append('queryParams', serializeTransit(queryParams));
  }

  const url = `${apiBaseUrl()}${SDK_IMAGES_UPLOAD_ENDPOINT}`;

  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      // NOTE: do NOT set Content-Type explicitly — the browser must set it
      // (including the multipart boundary) for FormData bodies.
      body: formData,
    })
    .then(res => {
      if (res.status >= 400) {
        return throwLocalApiError(res);
      }
      return parseSuccessResponse(res);
    });
};

/**
 * Create the client-side proxy SDK. Drop-in replacement for
 * `sdkLoader.createInstance(...)` at the browser instantiation site
 * (src/index.js). All requests are routed through our Express server,
 * which holds the Sharetribe token server-side in an httpOnly session.
 */
export const createProxySdk = () => {
  const resources = buildResourceFacades();
  return {
    ...resources,
    ...buildTopLevelOps(),
    currentUser: {
      ...resources.currentUser,
      create: currentUserCreate,
    },
    images: {
      upload: imagesUpload,
    },
    login,
    logout,
    authInfo,
  };
};

export default createProxySdk;
